/**
 * The editor's version: apps/editor/package.json, injected at build time by vite.config.ts (`define`). Empty where
 * the define is absent (unit tests run without Vite's define), and the label is then not shown. The label opens the
 * release notes (releases.ts).
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { t } from './i18n.ts';
import { Modal } from './panels/Dialogs.tsx';
import { RELEASES, type Release } from './releases.ts';

declare const __EDITOR_VERSION__: string | undefined;

export const EDITOR_VERSION: string = typeof __EDITOR_VERSION__ === 'string' ? __EDITOR_VERSION__ : '';

/** A small, muted version label (`v1.0.0`) that opens the release notes; nothing when the version is unknown. */
export function VersionLabel({ className = '' }: { className?: string }) {
  const [open, setOpen] = useState(false);
  if (!EDITOR_VERSION) return null;
  const name = t('app.versionTitle', { version: EDITOR_VERSION });
  return (
    <>
      <button type="button" className={`app-version ${className}`.trim()} data-testid="app-version" title={name} aria-label={name} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        {t('app.version', { version: EDITOR_VERSION })}
      </button>
      {open && <ReleaseNotesDialog version={EDITOR_VERSION} onClose={() => setOpen(false)} />}
    </>
  );
}

const releaseText = (r: Release): string => [r.version, r.title, ...r.summary, ...r.features, ...r.fixes].join('\n').toLowerCase();

/**
 * The release notes: the newest release opens to its summary, the others show their heading until opened; "Show
 * details" lists what a release added and fixed. A search shows the releases that mention it, opened.
 */
export function ReleaseNotesDialog({ version, onClose, releases = RELEASES }: { version: string; onClose: () => void; releases?: Release[] }) {
  const [query, setQuery] = useState('');
  const [brief, setBrief] = useState<ReadonlySet<string>>(() => new Set(releases[0] ? [releases[0].version] : []));
  const [details, setDetails] = useState<ReadonlySet<string>>(() => new Set());
  // While searching, every release found is open with its details; this holds the details closed during the search.
  const [searchHidden, setSearchHidden] = useState<ReadonlySet<string>>(() => new Set());
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (q ? releases.filter((r) => releaseText(r).includes(q)) : releases), [q, releases]);
  const toggle = (set: ReadonlySet<string>, v: string) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    return next;
  };
  return (
    <Modal title={t('releases.title', { version })} onClose={onClose} testId="release-notes" labelledBy="release-notes-title">
      <input type="search" className="release-search" value={query} onChange={(e) => {
          setQuery(e.target.value);
          setSearchHidden(new Set());
        }} placeholder={t('releases.search')} aria-label={t('releases.search')} data-testid="release-notes-search" />
      {shown.length === 0 && <p className="hint">{t('releases.none', { query: query.trim() })}</p>}
      {shown.map((r) => {
        const isBrief = q ? true : brief.has(r.version);
        const isDetails = q ? !searchHidden.has(r.version) : details.has(r.version);
        return (
          <section key={r.version} className="release" data-testid={`release-${r.version}`}>
            <h3 className="release-head">
              <button type="button" aria-expanded={isBrief} onClick={() => setBrief((s) => toggle(s, r.version))} disabled={!!q} data-testid="release-toggle">
                {isBrief ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
                <span className="release-version">{r.version}</span>
                <span>{r.title}</span>
              </button>
              <span className="release-date">{r.date}</span>
            </h3>
            {isBrief && (
              <>
                <ul className="release-summary" data-testid="release-summary">
                  {r.summary.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
                {r.features.length + r.fixes.length > 0 && (
                  <button type="button" className="btn ghost small" aria-expanded={isDetails} onClick={() => (q ? setSearchHidden : setDetails)((s) => toggle(s, r.version))} data-testid="release-details-toggle">
                    {isDetails ? t('releases.hideDetails') : t('releases.showDetails')}
                  </button>
                )}
                {isDetails && (
                  <div className="release-details" data-testid="release-details">
                    {r.features.length > 0 && (
                      <>
                        <h4>{t('releases.features')}</h4>
                        <ul>
                          {r.features.map((f) => (
                            <li key={f}>{f}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {r.fixes.length > 0 && (
                      <>
                        <h4>{t('releases.fixes')}</h4>
                        <ul>
                          {r.fixes.map((f) => (
                            <li key={f}>{f}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </section>
        );
      })}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose} data-testid="release-notes-close">
          {t('dialog.close')}
        </button>
      </div>
    </Modal>
  );
}
